import type { Feature, FeatureCollection, MultiPolygon, Position } from "geojson";
import type { GeometryResult } from "../types";

export interface PolygonDeleteTarget {
	featureIndex: number;
	pathIndex: number;
}

/**
 * Remove multiple features by index from a FeatureCollection.
 * Sorts indices descending internally to avoid index shift issues.
 */
export function batchDeletePolygons(
	fc: FeatureCollection,
	featureIndices: number[],
): GeometryResult<FeatureCollection> {
	for (const idx of featureIndices) {
		if (idx < 0 || idx >= fc.features.length) {
			return { ok: false, error: `Feature index ${idx} out of bounds` };
		}
	}
	const removeSet = new Set(featureIndices);
	return {
		ok: true,
		data: {
			type: "FeatureCollection",
			features: fc.features.filter((_, i) => !removeSet.has(i)),
		},
	};
}

/**
 * Delete specific sub-polygons (by pathIndex) from features in a FeatureCollection.
 * Handles MultiPolygon sub-polygon removal, Polygon whole-feature removal,
 * and downgrades MultiPolygon→Polygon when only 1 sub-polygon remains.
 */
export function batchDeletePolygonTargets(
	fc: FeatureCollection,
	targets: PolygonDeleteTarget[],
): GeometryResult<FeatureCollection> {
	// Validate
	for (const t of targets) {
		if (t.featureIndex < 0 || t.featureIndex >= fc.features.length) {
			return { ok: false, error: `Feature index ${t.featureIndex} out of bounds` };
		}
	}

	if (targets.length === 0) {
		return { ok: true, data: { type: "FeatureCollection", features: [...fc.features] } };
	}

	// Group pathIndices by featureIndex
	const grouped = new Map<number, Set<number>>();
	for (const t of targets) {
		let s = grouped.get(t.featureIndex);
		if (!s) {
			s = new Set();
			grouped.set(t.featureIndex, s);
		}
		s.add(t.pathIndex);
	}

	const newFeatures: Feature[] = [];
	for (let i = 0; i < fc.features.length; i++) {
		const feature = fc.features[i];
		const pathIndicesToRemove = grouped.get(i);

		if (!pathIndicesToRemove) {
			newFeatures.push(feature);
			continue;
		}

		if (feature.geometry.type === "MultiPolygon") {
			const mp = feature.geometry as MultiPolygon;
			const remaining = mp.coordinates.filter((_, pi) => !pathIndicesToRemove.has(pi));

			if (remaining.length === 0) {
				// Remove feature entirely
				continue;
			} else if (remaining.length === 1) {
				// Downgrade to Polygon — construct explicitly (no stale bbox)
				newFeatures.push({
					type: "Feature",
					properties: feature.properties,
					geometry: { type: "Polygon", coordinates: remaining[0] },
				});
			} else {
				// Still MultiPolygon — construct explicitly (no stale bbox)
				newFeatures.push({
					type: "Feature",
					properties: feature.properties,
					geometry: { type: "MultiPolygon", coordinates: remaining },
				});
			}
		} else {
			// Polygon or other — remove entire feature
			continue;
		}
	}

	return { ok: true, data: { type: "FeatureCollection", features: newFeatures } };
}

/**
 * Translate multiple polygon features by a [lng, lat] delta.
 */
export function batchMovePolygons(
	fc: FeatureCollection,
	featureIndices: number[],
	delta: Position,
): GeometryResult<FeatureCollection> {
	for (const idx of featureIndices) {
		if (idx < 0 || idx >= fc.features.length) {
			return { ok: false, error: `Feature index ${idx} out of bounds` };
		}
	}
	const moveSet = new Set(featureIndices);
	return {
		ok: true,
		data: {
			type: "FeatureCollection",
			features: fc.features.map((f, i) => {
				if (!moveSet.has(i)) return f;
				const geom = f.geometry;
				if (geom.type === "Polygon") {
					return {
						...f,
						geometry: {
							type: "Polygon" as const,
							coordinates: geom.coordinates.map((ring) =>
								ring.map((coord) => [coord[0] + delta[0], coord[1] + delta[1]]),
							),
						},
					};
				}
				if (geom.type === "MultiPolygon") {
					return {
						...f,
						geometry: {
							type: "MultiPolygon" as const,
							coordinates: geom.coordinates.map((poly) =>
								poly.map((ring) => ring.map((coord) => [coord[0] + delta[0], coord[1] + delta[1]])),
							),
						},
					};
				}
				return f;
			}),
		},
	};
}
